import { Component } from '@theme/component';

/**
 * Perks panels that swap based on the active tab in a sibling <community-tabs>.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} panels - The perk panel elements.
 *
 * @extends {Component<Refs>}
 */
class TabLinkedPerks extends Component {
  /** @type {HTMLElement | null} */
  #tabsEl = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#linkToTabs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #linkToTabs() {
    const targetId = this.dataset.targetSectionId;
    /** @type {HTMLElement | null} */
    let tabsEl = null;

    if (targetId) {
      // CSS.escape is for identifier contexts (after #/.), not for inside [attr="..."].
      // Escape backslash and double-quote manually for the attribute-value branch.
      const attrSafe = targetId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      tabsEl = /** @type {HTMLElement | null} */ (
        document.querySelector(`#community-tabs-${CSS.escape(targetId)}, community-tabs[id$="${attrSafe}"]`)
      );
    }

    if (!tabsEl) {
      // Auto-link: prefer the closest preceding community-tabs in DOM order, else the first on the page.
      const all = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('community-tabs'));
      for (const el of all) {
        if (el.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING) {
          tabsEl = el;
        }
      }
      if (!tabsEl && all.length > 0) tabsEl = all[0];
    }

    if (!tabsEl) return;
    this.#tabsEl = tabsEl;

    // Sync to the currently-active tab on load.
    const activeTab = tabsEl.querySelector('[role="tab"][aria-selected="true"]');
    if (activeTab instanceof HTMLElement) {
      this.#syncToIndex(activeTab.dataset.index);
    }

    // React to subsequent tab changes.
    this.#observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'attributes' || m.attributeName !== 'aria-selected') continue;
        const target = m.target;
        if (!(target instanceof HTMLElement)) continue;
        if (target.getAttribute('aria-selected') !== 'true') continue;
        this.#syncToIndex(target.dataset.index);
      }
    });

    this.#observer.observe(tabsEl, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected'],
    });
  }

  /** @param {string | undefined} index */
  #syncToIndex(index) {
    if (index == null) return;
    const panels = this.refs.panels;
    if (!panels) return;
    for (const panel of panels) {
      const isActive = panel.dataset.index === index;
      panel.classList.toggle('tab-linked-perks__panel--active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    }
  }
}

customElements.define('tab-linked-perks', TabLinkedPerks);
